import { allowedTrackingParams } from "@repo/design-system/utils/url-tracking-params";

// eon-specific: also forward _pc (used in older tracking setups)
const eonTrackingParams = [...allowedTrackingParams, "_pc"];

export function isOcaApplyLink(applyUrl: string): boolean {
  return applyUrl.includes("oca.cfapps.eu10");
}

function isEmailLink(applyUrl: string): boolean {
  return applyUrl.startsWith("mailto:") || applyUrl.includes("@");
}

function buildPipelineValue(referrer: string, gaId: string | null): string {
  const base = referrer || "Jobboard";
  return gaId ? `${base}(${gaId})` : base;
}

function addPipelineToOca(
  url: URL,
  pipelineValue: string,
  gaId: string | null
): void {
  const hashParams = new URLSearchParams(url.hash.slice(2));

  if (gaId && hashParams.has("jobPipeline")) {
    const existing = decodeURIComponent(hashParams.get("jobPipeline") ?? "");
    if (!existing.includes(gaId)) {
      hashParams.set("jobPipeline", `${existing}(${gaId})`);
    }
  } else if (!hashParams.has("jobPipeline")) {
    hashParams.set("jobPipeline", pipelineValue);
  }

  url.hash = `/?${hashParams.toString()}`;
}

function addPipelineToUrl(
  url: URL,
  pipelineValue: string,
  gaId: string | null
): void {
  if (gaId && url.searchParams.has("jobPipeline")) {
    const existing = decodeURIComponent(
      url.searchParams.get("jobPipeline") ?? ""
    );
    if (!existing.includes(gaId)) {
      url.searchParams.set("jobPipeline", `${existing}(${gaId})`);
    }
  } else if (!url.searchParams.has("jobPipeline")) {
    url.searchParams.set("jobPipeline", pipelineValue);
  }
}

// For OCA links: UTM params go into the hash, utm_source defaults to "jobboard"
function addOcaTrackingParams(
  url: URL,
  currentSearchParams: URLSearchParams
): void {
  const hashParams = new URLSearchParams(url.hash.slice(2));
  const effective = new URLSearchParams(currentSearchParams);

  if (!effective.has("utm_source")) {
    effective.set("utm_source", "jobboard");
  }

  for (const param of eonTrackingParams) {
    const value = effective.get(param);
    if (value && !hashParams.has(param)) {
      hashParams.append(param, value);
    }
  }

  url.hash = `/?${hashParams.toString()}`;
}

// For eon_oracle: utm_source defaults to "fsx", utm_medium to "third+party"
function addEonOracleTrackingParams(
  url: URL,
  currentSearchParams: URLSearchParams
): void {
  if (
    !(
      url.searchParams.has("utm_source") ||
      currentSearchParams.has("utm_source")
    )
  ) {
    url.searchParams.set("utm_source", "fsx");
  }
  if (
    !(
      url.searchParams.has("utm_medium") ||
      currentSearchParams.has("utm_medium")
    )
  ) {
    url.searchParams.set("utm_medium", "third+party");
  }

  for (const param of eonTrackingParams) {
    const value = currentSearchParams.get(param);
    if (value && !url.searchParams.has(param)) {
      url.searchParams.append(param, value);
    }
  }
}

export function processApplyUrl(
  applyUrl: string,
  options: {
    source?: string;
    addPipeline?: boolean;
    referrer?: string;
    gaId?: string | null;
    currentSearchParams?: URLSearchParams;
    currentLocale?: string;
    postingLanguages?: string[];
  } = {}
): string {
  if (isEmailLink(applyUrl)) {
    return applyUrl;
  }

  // When eon is fully migrated this can be removed
  const isEonOracle =
    options.source === "eon_oracle" ||
    options.source === "eon_oracle_dev" ||
    options.source === "eon_oracle_sandbox" ||
    options.source === "eon_oracle_new_development" ||
    options.source === "eon_oracle_new";
  const localizedApplyUrl =
    isEonOracle && options.currentLocale && options.postingLanguages
      ? replaceLocalParam(
          applyUrl,
          options.currentLocale,
          options.postingLanguages
        )
      : applyUrl;
  const url = new URL(localizedApplyUrl);
  const currentSearchParams =
    options.currentSearchParams ?? new URLSearchParams();
  const isOca = isOcaApplyLink(localizedApplyUrl);

  if (options.addPipeline) {
    const pipelineValue = buildPipelineValue(
      options.referrer ?? "",
      options.gaId ?? null
    );
    if (isOca) {
      addPipelineToOca(url, pipelineValue, options.gaId ?? null);
    } else {
      addPipelineToUrl(url, pipelineValue, options.gaId ?? null);
    }
  }

  if (isOca) {
    addOcaTrackingParams(url, currentSearchParams);
  } else if (isEonOracle) {
    addEonOracleTrackingParams(url, currentSearchParams);
  }

  return url.toString();
}

// Another customer request – the external applyform is
// available in all languages. Still we should redirect only to
// the languages the job is actually posted in. Should only modify eon_oracle
// apply urls.
export function replaceLocalParam(
  applyUrl: string,
  currentLocale: string,
  postingLanguages: string[]
): string {
  let locale = currentLocale;
  if (!postingLanguages.includes(locale) && postingLanguages.length >= 1) {
    locale = postingLanguages[0];
  }
  return applyUrl.replace("/en/", `/${locale}/`);
}
